import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { arrayToObj, nextId } from "../../utils";
import { client } from "../../api/client";
import React from "react";


const calculatorsAdapter = createEntityAdapter();

const initialState = calculatorsAdapter.getInitialState({
	schema: [
		{
			label: "Mortgage Calculator",
			path: "/mortgage",
			element: "mortgage",
			description: "One of the benefits of being a Choice Franchisee is access to our Financing resources. This calculator will ensure our Financing team has a strong foundation to assist you with the financing process.",
			instructions: "<b>Instructions</b><ul><li>Input your contact information at the top. *All fields are required</li><li>Input your portfolio data below, one property at a time. You may enter up to (4) levels of debt per property and up to (10) properties.</li><li>Upon your submission, a member of our Financing team will be in touch with you to assist in preparing your portfolio for the lending process and work to match your debt needs with a potential lender.</li></ul>Should you have any questions, please contact us at <a style='color: #00558C;' href='mailto:profit@choicehotels.com'>profit@choicehotels.com</a>."
		},
		{
			label: "Profit Projector",
			path: "/cost-per-&-profit",
			element: "costPer",
			description: "Are you able to project the appropriate RevPAR to drive more profitability? This tool will take the revenue and expenses figures you input and project your profitability point.",
			instructions: "<b>Instructions</b><ol><li>Log into Choice Advantage. Under the Run pull down menu select Reports. Choose the \"Hotel Statistics\" report and enter the start date and end date that match the time period covered by your P&L. Remember to use a P&L that is covering an entire year. Hit submit. From the \"Date Range\" column write down the numbers in the \"Total Occupied Rooms\" and \"ADR for Total Occupied Rooms\" rows.</li><li>Go to the first expense line item on your P&L and determine if it is Marginal, Fixed, or Mixed. Write the expense title in the first open line under the appropriate section and enter the expense total in the adjacent column. If the expense is mixed, you will fill out the column indicating the percentage of the expense which is variable. The calculator will automaticall split the Mixed expenses into Marginal Cost per Room and Fixed Cost per Room. If the expense is fully Marginal, the calculator will automatically calculate the Marginal Cost per Room for that expense.</li><li>Review your second expense and follow the same process described above. As you enter your expenses, the columns will automatically total and the Marginal Cost per Room and Break-even will start calculating. Continue down your P&L until all expenses are listed on the Calculator tab. Note your Marginal Cost per Room and Break-even Point for the year. Save the Calculator so you can use it as a benchmark next year.</li></ol>* Save yourself time next year but duplicating this workbook. This way you only need to update the expense line totals and not the titles. You will also ensure consistancy since expenses will remain in the same columns.",
			about: "<b>Marginal Cost per Room</b><br />Marginal costs are those expenses associated with renting a room. They increase when you rent more rooms. Think of your Marginal Cost as the cost to rent one more room. Which costs would increase if you sold 66 rooms tonight instead of 65? Marginal costs are the opposite of fixed costs. Fixed costs are those expenses you pay no matter how many rooms you rent. There are some expenses with fixed and variable portions. These expenses will generate a bill even if you sell zero rooms but the bill will increase as you sell more rooms. An example would be your electicity bill. We will call these \"mixed\" expenses and will estimate the percentage of the expense which is variable.<br /><br /><b>Break-even Point</b><br />Once you calculate your Marginal Cost per Room for a year, the calculator will combine that with your total fixed costs, occupancy, and ADR for that year to come up with your Break-even Point. Your Break-even Point is the number of rooms you needed to rent in order to cover all of your costs. Now that you know your Break-even Point, you can adjust the \"ADR,\" \"Fixed Cost,\" or \"Marginal Cost per Room\" fields at the top of the sheet to do \"what-if\" scenerios to see how your Break-even Point changes. (Note that overwritting the fixed and marginal cost fields will erase formulas. Save your spreadsheet before proceeding)<br /><br /><b>Warnings</b><br />Use your Annual P&L when completing the calculators (12 month period). Though calendar year is recommended, Rolling 12 can be used. Using data covering a shorter time period will lead to skewed results due to seasonality. Use the calculators with caution if your property has only been open one year as costs will run high. Results should not be benchmarked against more mature properties but could be used as a baseline for future year performance. Please remember that Marginal Cost per Room is not the same as Cost Per Occupied Room (CPOR).<br /><br />Should you have any questions, please contact your Area Director or the Profit team at <a style='color: #00558C;' href='mailto:profit@choicehotels.com'>profit@choicehotels.com</a>."
		},
		{
			label: "Marginal Costs Channel Mix Calculator",
			path: "/channel-mix",
			element: "channelMix",
			description: "The Channel Mix Calculator is an easy-to-use tool to project many of the costs associated with selling a room through different revenue channels.",
			instructions: "<b>Instructions</b><p>Enter the following data points to calculate the Nightly and Weekly marginal costs. This is just to show the potential revenue for taking any given rate. And to also show that even a low rate can still have profit within it. (Weekly stays utilize the extended stay model with limited housekeeping services.)</p>",
			about: "Marginal costs are those expenses associated with renting a room. They increase when you rent more rooms. Marginal costs are the opposite of fixed costs. Fixed costs are those expenses you pay no matter how many rooms you rent.<br /><br />Typical marginal expenses have been entered below. Insert the amount your hotel pays for each line item. If your hotel has additional marginal costs, you may add those as well. Remember, the test of a marginal expense is do you have to pay it even if you don't rent a room.<br /><br />Should you have any questions, please contact your Area Director or the Profit team at <a style='color: #00558C;' href='mailto:profit@choicehotels.com'>profit@choicehotels.com</a>.",
			outputs: [
				{
					label: "Marginal Cost",
					type: "cost",
					headers: [
						{label: "Booking Channel"},
						{label: "Hotel Direct / CH.com Non-CP"},
						{label: "Hotel Direct / CH.com CP"},
						{label: "GDS CP"},
						{label: "GDS Non-CP"},
						{label: "Booking.com"},
						{label: "Expedia"},
						{label: "Opaque 35%"},
						{label: "Opaque 50%"}
					],
					instructions: "Marginal costs are those expenses associated with renting a room. They increase when you rent more rooms. Marginal costs are the opposite of fixed costs. Fixed costs are those expenses you pay no matter how many rooms you rent.<br /><br /><b>Marginal Costs</b><br />Typical marginal expenses have been entered below. Insert the amount your hotel pays for each line item. If your hotel has additional marginal costs, you may add those as well. Remember, the test of a marginal expense is do you have to pay it even if you don't rent a room.",
					footnote: "<p><sup>1</sup> Franchise fees are estimated in this calculator at 9% and may vary by contract. This estimate only includes the system fee and royalties, which are based on revenue, and not fixed costs such as property support.</p><p><sup>2</sup> Commission is based on a standard 10% commission. Note, some CH.com bookings may incur a 10% CHERP commission if applicable, which is not included in the calculator above.</p><p><sup>3</sup> GDS fee is $7.70 per booking. Cost shown here is for a one-night stay. A 2 night stay or longer would still only incur a single $7.70 fee GDS Avg LOS is 2.8 nights. Opaque will transfer later in 2021 to Transaction Fee</p><p><sup>4</sup> OTA transaction (similar to GDS) incurs a $4.00 per consumed stay fee. $3 for upscale and WS and $1 for EL and RW. OTA Avg LOS 1.8 Nights</p><p><sup>5</sup> Credit card processing fee is 2%</p>Should you have any questions, please contact your Area Director or the Profit team at <a style='color: #00558C;' href='mailto:profit@choicehotels.com'>profit@choicehotels.com</a>."
				},
				{
					label: "Weekly Marginal Cost",
					type: "weekly",
					headers: [
						{label: ""},
						{label: "Sunday"},
						{label: "Monday"},
						{label: "Tuesday"},
						{label: "Wednesday"},
						{label: "Thursday"},
						{label: "Friday"},
						{label: "Saturday"},
						{label: "Weekly Totals"}
					],
					instructions: "Marginal costs are those expenses associated with renting a room. They increase when you rent more rooms. Marginal costs are the opposite of fixed costs. Fixed costs are those expenses you pay no matter how many rooms you rent. When renting to extended stays, offering limited housekeeping services can help reduce some marginal costs, including housekeeping and laundry labor and the chemicals and guest room supplies associated with a daily room service.<br /><br />To estimate the marginal costs associated with a reduced housekeeping schedule, check the box on the days where full houskeeping service and light touch (trash and towels) will be done.",
					footnote: "<p><sup>1</sup> Franchise fees are estimated in this calculator at 9% and may vary by contract. This estimate only includes the system fee and royalties, which are based on revenue, and not fixed costs such as property support.</p><p><sup>2</sup> Commission is based on a standard 10% commission. Note, some CH.com bookings may incur a 10% CHERP commission if applicable, which is not included in the calculator above.</p><p><sup>3</sup> Credit card processing fee is 2%</p>Should you have any questions, please contact your Area Director or the Profit team at <a style='color: #00558C;' href='mailto:profit@choicehotels.com'>profit@choicehotels.com</a>."
				}
			]
		}
	],
	view: "mortgage",
	status: "idle",
	current: 0,
	email: {message: "", severity: "success"},
	account: {fname: "", lname: "", email: "", phone: ""}
});

// Thunk functions
export const fetchCalculator = createAsyncThunk("calculators/fetchCalculator", async type => {
	let data = new FormData();
	data.append("type", type);
	data.append("mode", "list");
	const response = await client.post("./api.php", data);
	return arrayToObj(response, "name");
});
export const saveCalculator = createAsyncThunk("calculators/saveCalculator", async params => {
	const { type, name, json } = params;
	let data = new FormData();
	data.append("type", type);
	data.append("mode", "save");
	data.append("name", name);
	data.append("json", json);
	await client.post("./api.php", data);
	return name;
});
export const loadCalculator = createAsyncThunk("calculators/loadCalculator", async params => {
	const { id, type, name } = params;
	let data = new FormData();
	data.append("type", type);
	data.append("mode", "load");
	data.append("name", name);
	const response = await client.post("./api.php", data);
	return {id, type, json: response};
});
export const fetchAccount = createAsyncThunk("calculators/fetchAccount", async () => {
	let data = new FormData();
	data.append("mode", "user");
	return await client.post("./api.php", data);
});
export const sendEmail = createAsyncThunk("calculators/sendEmail", async params => {
	const { blob, fname, lname, email, phone } = params;
	let data = new FormData();
	data.append("mode", "email");
	data.append("pName", `${fname} ${lname}`);
	data.append("pEmail", email);
	data.append("pPhone", phone);
	data.append("pdf", blob);
	return await client.post("./api.php", data);
});

const calculatorsSlice = createSlice({
	name: "calculators",
	initialState,
	reducers: {
		updateAccount(state, action) {
			state.account = action.payload;
		},
		changeView(state, action) {
			state.view = action.payload;
		},
		setCurrent(state, action) {
			state.current = action.payload;
		},
		clearCalculators(state) {
			const allIds = Object.values(state.entities).map((calculator) => calculator.id);
			calculatorsAdapter.removeMany(state, allIds);
		},
		setStatus(state, action) {
			state.status = action.payload;
		}
	},
	extraReducers: builder => {
		builder
			.addCase(fetchCalculator.pending, (state) => {
				state.status = "loading";
			})
			.addCase(saveCalculator.pending, (state) => {
				state.status = "loading";
			})
			.addCase(fetchCalculator.rejected, (state) => {
				state.status = "error";
			})
			.addCase(saveCalculator.rejected, (state) => {
				state.status = "error";
			})
			.addCase(loadCalculator.rejected, (state) => {
				state.status = "error";
			})
			.addCase(fetchAccount.rejected, (state) => {
				state.status = "error";
			})
			.addCase(sendEmail.rejected, (state) => {
				state.status = "error";
			})
			.addCase(fetchCalculator.fulfilled, (state, action) => {
				const loadedVersions = action.payload;
				if(!!Object.keys(loadedVersions).length) {
					calculatorsAdapter.setAll(state, loadedVersions);
				}
				state.status = "idle";
			})
			.addCase(saveCalculator.fulfilled, (state, action) => {
				const existing = Object.values(state.entities).filter((calculator) => calculator.name.toLowerCase() === action.payload.toLowerCase())[0];
				const id = existing ? existing.id : nextId(state.ids);
				const newVersion = {
					id,
					name: action.payload
				};
				calculatorsAdapter.upsertOne(state, newVersion);
				state.status = "idle";
				state.current = id + 1;
			})
			.addCase(loadCalculator.fulfilled, (state, action) => {
				if(action.payload.type === "mortgage") {
					state.account = JSON.parse(action.payload.json).account;
				}
			})
			.addCase(fetchAccount.fulfilled, (state, action) => {
				const fields = ["fname", "lname", "email"];
				const acct = fields.reduce((obj, name) => ({...obj, [name]: action.payload[name]}), {});
				state.account = {...state.account, ...acct};
			})
			.addCase(sendEmail.fulfilled, (state, action) => {
				const { success } = action.payload;
				state.email = {
					message: success ? "Email successfully sent!" : "Email not sent...",
					severity: success ? "success" : "error"
				};
			})
	}
})

export const {
	updateAccount,
	changeView,
	setCurrent,
	clearCalculators,
	setStatus
} = calculatorsSlice.actions;

export default calculatorsSlice.reducer

export const { selectAll: selectCalculators } = calculatorsAdapter.getSelectors(state => state.calculators);